import { Helmet } from "react-helmet-async";

export interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SeoProps {
  title: string;
  description: string;
  canonical: string;
  /** Optional additional JSON-LD schema object (e.g. Service, FAQPage). */
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Breadcrumb trail. If provided, a BreadcrumbList JSON-LD is emitted. */
  breadcrumbs?: BreadcrumbItem[];
}

export default function Seo({
  title,
  description,
  canonical,
  schema,
  breadcrumbs,
}: SeoProps) {
  const schemas: Array<Record<string, unknown>> = [];
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    });
  }
  if (schema) {
    if (Array.isArray(schema)) schemas.push(...schema);
    else schemas.push(schema);
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

const PROVIDER = {
  "@type": "Organization",
  name: "Zweering Elektrotechnik GmbH",
  url: "https://zweering.de",
  address: {
    "@type": "PostalAddress",
    streetAddress: "An der Schleuse 10",
    postalCode: "46446",
    addressLocality: "Emmerich am Rhein",
    addressCountry: "DE",
  },
};

export function serviceSchema(name: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    areaServed: "Emmerich am Rhein und Umgebung",
    provider: PROVIDER,
  };
}

export function faqSchema(items: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}
